import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, Crosshair } from 'lucide-react'
import { Toaster, toast } from 'sonner'
import { VISIBLE_FISHES, type FishDef } from '@/data/fish'
import { useAquarium } from '@/hooks/useAquarium'
import { useAudio } from '@/hooks/useAudio'
import { useIsMobile } from '@/hooks/use-mobile'
import { Tank } from '@/components/Tank'
import { RosterPanel } from '@/components/RosterPanel'
import { UserCard } from '@/components/UserCard'
import { StatsPanel } from '@/components/StatsPanel'
import { ActionBar } from '@/components/ActionBar'
import { ActionBarMobile } from '@/components/ActionBarMobile'
import { FishDrawer } from '@/components/FishDrawer'
import { StatsDrawer } from '@/components/StatsDrawer'
import { CodexDialog } from '@/components/CodexDialog'
import { ShopDialog } from '@/components/ShopDialog'
import { DecorationInventory } from '@/components/DecorationInventory'
import { BackgroundUpload } from '@/components/BackgroundUpload'
import { AmbientSelector } from '@/components/AmbientSelector'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { type Decoration } from '@/data/decorations'

export default function App() {
  const [fish, setFish] = useState<FishDef>(VISIBLE_FISHES[0])
  const [followMode, setFollowMode] = useState(false)
  const [feedSignal, setFeedSignal] = useState(0)
  const [codexOpen, setCodexOpen] = useState(false)
  const [lightShow, setLightShow] = useState(false)
  const [shopOpen, setShopOpen] = useState(false)
  const [decorationOpen, setDecorationOpen] = useState(false)
  const [fishDrawerOpen, setFishDrawerOpen] = useState(false)
  const [statsDrawerOpen, setStatsDrawerOpen] = useState(false)
  const [selectedFeed, setSelectedFeed] = useState<'normal' | 'highProtein' | 'colorEnhance' | 'probiotic' | 'golden'>('normal')
  const [selectedDecoration, setSelectedDecoration] = useState<Decoration | null>(null)
  const [backgroundOpen, setBackgroundOpen] = useState(false)
  const [ambientOpen, setAmbientOpen] = useState(false)
  const aq = useAquarium()
  const audio = useAudio()
  const stats = aq.statsMap[fish.id]
  const lastLevelTick = useRef(0)
  const isMobile = useIsMobile()

  useEffect(() => {
    if (aq.levelUpTick > lastLevelTick.current) {
      lastLevelTick.current = aq.levelUpTick
      toast.success(`🎉 馆长升级!当前 LVL ${aq.xpState.level}`, {
        description: '继续悉心照料,解锁更多神秘鱼种',
      })
      audio.play('positive')
    }
  }, [aq.levelUpTick, aq.xpState.level, audio])

  const prevSoundType = useRef('')
  useEffect(() => {
    if (aq.soundType && aq.soundType !== prevSoundType.current) {
      audio.play(aq.soundType)
      prevSoundType.current = aq.soundType
    }
  }, [aq.soundTrigger, aq.soundType, audio])

  useEffect(() => {
    const gotReward = aq.checkLoginReward()
    if (gotReward) {
      toast.success('🎁 登录奖励 +15 金币!', { description: '每天都来看看你的鱼儿吧' })
    }
  }, [aq.checkLoginReward])

  useEffect(() => {
    const handleFirstInteraction = () => {
      audio.unlock()
      document.removeEventListener('click', handleFirstInteraction)
      document.removeEventListener('touchstart', handleFirstInteraction)
    }
    document.addEventListener('click', handleFirstInteraction)
    document.addEventListener('touchstart', handleFirstInteraction)
    return () => {
      document.removeEventListener('click', handleFirstInteraction)
      document.removeEventListener('touchstart', handleFirstInteraction)
    }
  }, [audio])

  const hint = useMemo(() => {
    if (stats.satiety < 250) return '咕…肚子饿了,想吃东西'
    if (stats.satiety > 800) return '饱饱的~ 😋'
    if (aq.water < 250) return '水有点浑,该换水啦'
    if (stats.mood < 250) return '有点无聊,陪我玩嘛'
    if (stats.bond > 800 && stats.mood > 700) return '最喜欢你了!✨'
    return null
  }, [stats, aq.water])

  const doFeed = () => {
    aq.onEat(fish.id, selectedFeed)
    setFeedSignal((s) => s + 1)
  }

  const handleSelectDecoration = (decoration: Decoration) => {
    setSelectedDecoration(decoration)
  }

  const handlePlaceDecoration = (x: number, y: number) => {
    if (selectedDecoration) {
      aq.placeDecoration(selectedDecoration.id, x, y)
      toast.success(`已放置 ${selectedDecoration.name}`, { description: '装饰已添加到鱼缸' })
      setSelectedDecoration(null)
      audio.play('positive')
    }
  }

  const handleCancelPlacement = () => {
    setSelectedDecoration(null)
  }

  const handleRemoveDecoration = (index: number) => {
    aq.removeDecoration(index)
    toast.success('装饰已移除', { description: '已放回仓库' })
  }

  const handleBuyFeed = async (feedType: 'normal' | 'highProtein' | 'colorEnhance' | 'probiotic' | 'golden') => {
    const feed = {
      normal: 0,
      highProtein: 15,
      colorEnhance: 25,
      probiotic: 35,
      golden: 50,
    }[feedType]

    if (feed === 0) {
      setSelectedFeed(feedType)
      setShopOpen(false)
      toast.success('已选择普通颗粒', { description: '免费饲料已就绪' })
      return
    }

    const success = await aq.spendCoins(feed)
    if (success) {
      setSelectedFeed(feedType)
      setShopOpen(false)
      toast.success('购买成功!', { description: '饲料已添加到喂食槽' })
    } else {
      toast.error('金币不足', { description: '去逗鱼或换水赚取更多金币吧' })
    }
  }

  const handleBuyDecoration = async (decorationId: string) => {
    const success = await aq.buyDecoration(decorationId)
    if (success) {
      toast.success('购买成功!', { description: '装饰已添加到仓库' })
    } else {
      toast.error('金币不足', { description: '去逗鱼或换水赚取更多金币吧' })
    }
  }

  const doLightShow = () => {
    aq.onLightShow(fish.id)
    setLightShow(true)
    setTimeout(() => setLightShow(false), 2200)
  }

  const cycle = (dir: 1 | -1) => {
    const i = VISIBLE_FISHES.findIndex((f) => f.id === fish.id)
    setFish(VISIBLE_FISHES[(i + dir + VISIBLE_FISHES.length) % VISIBLE_FISHES.length])
  }

  if (isMobile) {
    return (
      <div className="h-screen w-screen overflow-hidden relative bg-gradient-to-br from-[#e9f3fa] via-[#dcebf6] to-[#cfe3f2]">
        <div className="absolute inset-0 hex-pattern opacity-60 pointer-events-none" />

        <div className="relative z-10 h-full flex flex-col">
          <div className="absolute top-3 left-3 z-30 flex items-center gap-2">
            <button
              onClick={() => setFishDrawerOpen(true)}
              className="w-10 h-10 rounded-xl bg-white/70 border border-white/80 backdrop-blur flex items-center justify-center text-[#2f5d8a] shadow"
            >
              <Crosshair size={20} />
            </button>
            <button
              onClick={() => setStatsDrawerOpen(true)}
              className="w-10 h-10 rounded-xl bg-white/70 border border-white/80 backdrop-blur flex items-center justify-center text-[#2f5d8a] shadow"
            >
              <span className="text-lg font-black">{fish.name}</span>
            </button>
          </div>

          <main className="flex-1 relative">
            <Tank
              fish={fish}
              lighting={aq.lighting}
              followMode={followMode}
              feedSignal={feedSignal}
              lowStatHint={hint}
              stats={stats}
              water={aq.water}
              decorations={aq.placedDecorations}
              selectedDecoration={selectedDecoration}
              backgroundState={aq.backgroundState}
              onEat={() => aq.onEat(fish.id)}
              onTease={() => aq.onTease(fish.id)}
              onPlaceDecoration={handlePlaceDecoration}
              onCancelPlacement={handleCancelPlacement}
              onPetTrickle={() => aq.onPetTrickle(fish.id)}
              growthScale={aq.getFishGrowthScale(fish.id)}
              growthStage={aq.getFishGrowthStage(fish.id)}
              fishLevelUpTick={aq.fishLevelUpTick}
            />

            <div className="absolute top-14 left-4 z-40 pointer-events-none">
              <AnimatePresence mode="wait">
                <motion.h2
                  key={fish.id}
                  className="text-2xl font-black tracking-[0.12em] text-[#2f5d8a] drop-shadow-md"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {fish.code}
                </motion.h2>
              </AnimatePresence>
              <div className="mt-1 flex flex-col gap-1">
                <span className="text-[10px] font-bold tracking-[0.35em] text-slate-400">
                  {fish.name} · {fish.species}
                </span>
                <div className="w-16 h-px bg-gradient-to-r from-slate-300 to-transparent" />
                <p className="text-xs text-slate-500 italic">{fish.quote}</p>
              </div>
            </div>

            <AnimatePresence>
              {lightShow && (
                <motion.div
                  className="absolute inset-0 z-20 pointer-events-none lightshow"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
              )}
            </AnimatePresence>

            <button
              onClick={() => cycle(-1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/60 border border-white/80 backdrop-blur flex items-center justify-center text-slate-500 hover:bg-white hover:text-sky-600 shadow transition-all active:scale-90"
              aria-label="上一条"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => cycle(1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/60 border border-white/80 backdrop-blur flex items-center justify-center text-slate-500 hover:bg-white hover:text-sky-600 shadow transition-all active:scale-90"
              aria-label="下一条"
            >
              <ChevronRight size={20} />
            </button>
          </main>

          <ActionBarMobile
            coins={aq.economy.coins}
            onOpenFishDrawer={() => setFishDrawerOpen(true)}
            onFeed={doFeed}
            onClean={() => { aq.onClean(); toast.success('水质焕然一新!💧') }}
            onTease={() => setFollowMode(true)}
            onToggleLight={aq.toggleLighting}
            onLightShow={doLightShow}
            lighting={aq.lighting}
            onOpenCodex={() => setCodexOpen(true)}
            onOpenShop={() => setShopOpen(true)}
            onOpenDecoration={() => setDecorationOpen(true)}
            onOpenBackground={() => setBackgroundOpen(true)}
            onOpenAmbient={() => setAmbientOpen(true)}
            onSave={() => { aq.saveGame(); toast.success('游戏已保存') }}
            soundEnabled={!audio.muted}
            onToggleSound={() => audio.setMuted((m) => !m)}
          />
        </div>

        <FishDrawer
          isOpen={fishDrawerOpen}
          selectedId={fish.id}
          onSelect={setFish}
          onClose={() => setFishDrawerOpen(false)}
          unlockedFish={aq.unlockedFish}
        />

        <StatsDrawer
          isOpen={statsDrawerOpen}
          fish={fish}
          stats={stats}
          water={aq.water}
          onFeed={doFeed}
          onClean={() => { aq.onClean(); toast.success('水质焕然一新!💧') }}
          onTease={() => setFollowMode(true)}
          onLightShow={doLightShow}
          onClose={() => setStatsDrawerOpen(false)}
        />

        <CodexDialog open={codexOpen} onOpenChange={setCodexOpen} selectedId={fish.id} onSelect={setFish} unlockedFish={aq.unlockedFish} />
        <ShopDialog
          open={shopOpen}
          onOpenChange={setShopOpen}
          coins={aq.economy.coins}
          purchasedDecorations={aq.purchasedDecorations}
          onBuyFeed={handleBuyFeed}
          onBuyDecoration={handleBuyDecoration}
        />
        <DecorationInventory
          open={decorationOpen}
          onOpenChange={setDecorationOpen}
          purchasedDecorations={aq.purchasedDecorations}
          placedDecorations={aq.placedDecorations}
          onSelectDecoration={handleSelectDecoration}
          onRemoveDecoration={handleRemoveDecoration}
        />
        <Dialog open={backgroundOpen} onOpenChange={setBackgroundOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>自定义背景</DialogTitle>
              <DialogDescription>上传照片作为鱼缸背景</DialogDescription>
            </DialogHeader>
            <BackgroundUpload
              currentBackground={aq.backgroundState}
              onUpdate={aq.updateBackground}
            />
          </DialogContent>
        </Dialog>
        <Dialog open={ambientOpen} onOpenChange={setAmbientOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>环境音效</DialogTitle>
              <DialogDescription>选择不同的背景音效营造氛围</DialogDescription>
            </DialogHeader>
            <AmbientSelector
              currentSound={aq.ambientSoundId}
              onSelect={(id) => {
                aq.updateAmbientSound(id as any)
                audio.setAmbientSoundId(id)
              }}
            />
          </DialogContent>
        </Dialog>
        <Toaster position="top-center" richColors />
      </div>
    )
  }

  return (
    <div className="h-screen w-screen overflow-hidden relative bg-gradient-to-br from-[#e9f3fa] via-[#dcebf6] to-[#cfe3f2]">
      <div className="absolute inset-0 hex-pattern opacity-60 pointer-events-none" />

      <div className="relative z-10 h-full flex gap-5 p-5">
        <div className="w-[290px] shrink-0 hidden lg:block">
          <RosterPanel selectedId={fish.id} onSelect={setFish} unlockedFish={aq.unlockedFish} />
        </div>

        <main className="flex-1 relative rounded-[28px] overflow-hidden border border-white/70 shadow-[inset_0_0_60px_rgba(255,255,255,0.5),0_20px_60px_rgba(70,140,220,0.18)]">
          <Tank
            fish={fish}
            lighting={aq.lighting}
            followMode={followMode}
            feedSignal={feedSignal}
            lowStatHint={hint}
            stats={stats}
            water={aq.water}
            decorations={aq.placedDecorations}
            selectedDecoration={selectedDecoration}
            backgroundState={aq.backgroundState}
            onEat={() => aq.onEat(fish.id)}
            onTease={() => aq.onTease(fish.id)}
            onPlaceDecoration={handlePlaceDecoration}
            onCancelPlacement={handleCancelPlacement}
            onPetTrickle={() => aq.onPetTrickle(fish.id)}
            growthScale={aq.getFishGrowthScale(fish.id)}
            growthStage={aq.getFishGrowthStage(fish.id)}
            fishLevelUpTick={aq.fishLevelUpTick}
          />

          <div className="absolute top-6 left-7 z-40 pointer-events-none">
            <div className="flex items-center gap-2.5">
              <Crosshair className="text-[#2f5d8a]" size={22} />
              <AnimatePresence mode="wait">
                <motion.h2
                  key={fish.id}
                  className="text-4xl font-black tracking-[0.12em] text-[#2f5d8a] drop-shadow-md"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {fish.code}
                </motion.h2>
              </AnimatePresence>
            </div>
            <div className="mt-2 ml-[34px] flex flex-col gap-2">
              <span className="text-xs font-bold tracking-[0.35em] text-slate-400">
                {fish.name} · {fish.species}
              </span>
              <div className="w-24 h-px bg-gradient-to-r from-slate-300 to-transparent" />
              <p className="text-sm text-slate-500 italic">{fish.quote}</p>
            </div>
          </div>

          <AnimatePresence>
            {lightShow && (
              <motion.div
                className="absolute inset-0 z-20 pointer-events-none lightshow"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            )}
          </AnimatePresence>

          <button
            onClick={() => cycle(-1)}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/60 border border-white/80 backdrop-blur flex items-center justify-center text-slate-500 hover:bg-white hover:text-sky-600 shadow transition-all active:scale-90"
            aria-label="上一条"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => cycle(1)}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/60 border border-white/80 backdrop-blur flex items-center justify-center text-slate-500 hover:bg-white hover:text-sky-600 shadow transition-all active:scale-90"
            aria-label="下一条"
          >
            <ChevronRight size={20} />
          </button>

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-30">
            <ActionBar
              followMode={followMode}
              lighting={aq.lighting}
              soundEnabled={!audio.muted}
              onToggleFollow={() => setFollowMode((m) => !m)}
              onFeed={doFeed}
              onClean={() => { aq.onClean(); toast.success('水质焕然一新!💧') }}
              onToggleLight={aq.toggleLighting}
              onLightShow={doLightShow}
              onOpenCodex={() => setCodexOpen(true)}
              onToggleSound={() => audio.setMuted((m) => !m)}
              onOpenShop={() => setShopOpen(true)}
              onOpenDecoration={() => setDecorationOpen(true)}
              onOpenBackground={() => setBackgroundOpen(true)}
              onOpenAmbient={() => setAmbientOpen(true)}
            />
          </div>
        </main>

        <div className="w-[320px] shrink-0 hidden xl:flex flex-col gap-4 min-h-0">
          <UserCard
            level={aq.xpState.level}
            xp={aq.xpState.xp}
            needed={aq.xpState.needed}
            onSave={() => { aq.saveGame(); toast.success('游戏已保存') }}
            onDeleteSave={() => {
              if (confirm('确定要删除存档吗？此操作不可撤销！')) {
                aq.deleteSave()
                window.location.reload()
              }
            }}
          />
          <div className="flex-1 overflow-y-auto min-h-0">
            <StatsPanel
              fish={fish}
              stats={stats}
              water={aq.water}
              onFeed={doFeed}
              onEnterFollow={() => setFollowMode(true)}
              onLightShow={doLightShow}
            />
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={fish.id}
              className="rounded-2xl bg-white/60 border border-white/80 backdrop-blur-md p-4"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <div className="text-xs font-black tracking-[0.25em] text-slate-400 mb-1.5">档案 DOSSIER</div>
              <p className="text-xs leading-relaxed text-slate-500">{fish.desc}</p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <CodexDialog open={codexOpen} onOpenChange={setCodexOpen} selectedId={fish.id} onSelect={setFish} unlockedFish={aq.unlockedFish} />
      <ShopDialog
        open={shopOpen}
        onOpenChange={setShopOpen}
        coins={aq.economy.coins}
        purchasedDecorations={aq.purchasedDecorations}
        onBuyFeed={handleBuyFeed}
        onBuyDecoration={handleBuyDecoration}
      />
      <DecorationInventory
        open={decorationOpen}
        onOpenChange={setDecorationOpen}
        purchasedDecorations={aq.purchasedDecorations}
        placedDecorations={aq.placedDecorations}
        onSelectDecoration={handleSelectDecoration}
        onRemoveDecoration={handleRemoveDecoration}
      />
      <Dialog open={backgroundOpen} onOpenChange={setBackgroundOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>自定义背景</DialogTitle>
            <DialogDescription>上传照片作为鱼缸背景</DialogDescription>
          </DialogHeader>
          <BackgroundUpload
            currentBackground={aq.backgroundState}
            onUpdate={aq.updateBackground}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={ambientOpen} onOpenChange={setAmbientOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>环境音效</DialogTitle>
            <DialogDescription>选择不同的背景音效营造氛围</DialogDescription>
          </DialogHeader>
          <AmbientSelector
            currentSound={aq.ambientSoundId}
            onSelect={(id) => {
              aq.updateAmbientSound(id as any)
              audio.setAmbientSoundId(id)
            }}
          />
        </DialogContent>
      </Dialog>
      <Toaster position="top-center" richColors />
    </div>
  )
}
